import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Space, Spin, Typography } from 'antd'
import { filter, get, isEmpty, isNil, join, map, size, some } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import {
  FormButtons,
  HistoryLink,
  ReduxMetadataTree,
  SearchHighlightedSpan,
  TypographyLinkWrapper,
} from '../../../common/components/sections'
import {
  StyledAlert,
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import {
  APP_ROUTES,
  EVENT_METADATA_TYPES,
  METADATA_TYPES,
  PERMISSIONS,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useDocumentTitle,
  useLatestEventByEntityTypesQuery,
  useUserHasPermissions,
} from '../../../common/hooks'

import { renderCuisineTypeEvent } from '../../helpers'
import {
  useCuisineTypesQuery,
  useDeleteCuisineTypeMutation,
  useReplaceCuisineTypeMutation,
  useRevertReplaceCuisineTypeMutation,
  useSaveCuisineTypesMutation,
  useSetIsCurationEnabledCuisineTypeMutation,
} from '../../hooks'
import {
  addCuisineTypeNode,
  expandCuisineTypeMainTreeKeys,
  expandCuisineTypeSupportingTreeKeys,
  moveCuisineTypes,
  renameCuisineTypeNode,
  resetCuisineTypeTree,
  setDataForRenamingCuisineType,
} from '../../redux/actions'

const CuisineTypesPage = () => {
  const dispatch = useDispatch()
  useDocumentTitle('Cuisine Types')
  const [isMoveActive, setIsMoveActive] = useState(false)
  const [isReplaceActive, setIsReplaceActive] = useState(false)

  const eventEntityTypes = useMemo(
    () => [EVENT_METADATA_TYPES.CUISINE_TYPE],
    [],
  )

  const {
    isLoading: isFetchLatestEventLoading,
    error: fetchLatestEventError,
    data: latestEvent,
    refetch: refetchLastEvent,
  } = useLatestEventByEntityTypesQuery(eventEntityTypes)

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    initialData,
  } = useCuisineTypesQuery()

  const [saveCuisineTypes, { isLoading: isSaveLoading, error: saveError }] =
    useSaveCuisineTypesMutation(refetchLastEvent)

  const [deleteCuisineType] = useDeleteCuisineTypeMutation(refetchLastEvent)

  const [setIsCurationEnabledCuisineType] =
    useSetIsCurationEnabledCuisineTypeMutation(refetchLastEvent)

  const [replaceCuisineType] = useReplaceCuisineTypeMutation(refetchLastEvent)

  const [revertReplaceCuisineType] =
    useRevertReplaceCuisineTypeMutation(refetchLastEvent)

  const {
    isDirty,
    data,
    dataForRenaming,
    mainTreeExpandedKeys,
    supportingTreeExpandedKeys,
  } = useSelector(state => state.cuisineTypes.cuisineTypes)

  const hasFullPermission = useUserHasPermissions([
    PERMISSIONS.METADATA_TREE.SAVE,
  ])

  const hasPermissionToViewDetails = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.GET,
  )

  const treeData = isDirty ? data : initialData

  const handleAdd = useCallback(
    node => dispatch(addCuisineTypeNode(node)),
    [dispatch],
  )

  const handleDelete = useCallback(
    ({ id }) => deleteCuisineType(id),
    [deleteCuisineType],
  )

  const handleRevertReplaceCuisineType = useCallback(
    ({ replaceToken }) => revertReplaceCuisineType(replaceToken),
    [revertReplaceCuisineType],
  )

  const handleSetIsCurationEnabled = useCallback(
    ({ id, isCurationEnabled }) =>
      setIsCurationEnabledCuisineType(id, isCurationEnabled),
    [setIsCurationEnabledCuisineType],
  )

  const handleRename = useCallback(
    ({ path, name }) => dispatch(renameCuisineTypeNode({ path, name })),
    [dispatch],
  )

  const handleSetDataForRenaming = useCallback(
    ({ id }) => dispatch(setDataForRenamingCuisineType({ id })),
    [dispatch],
  )

  const handleMove = useCallback(
    (nodesToMove, destinationNodes) => {
      const [destinationNode] = destinationNodes
      const levelToMoveUnder = destinationNode.level - 1

      const parentSelectedNodes = filter(
        nodesToMove,
        ({ parentId }) => !some(nodesToMove, ({ id }) => id === parentId),
      )

      const restrictedToMoveNodes = filter(parentSelectedNodes, node => {
        if (levelToMoveUnder > node.level) {
          return !isEmpty(node.children)
        }

        if (levelToMoveUnder === node.level) {
          return !isEmpty(
            node.level === 1
              ? get(node, 'children[0].children')
              : node.children,
          )
        }

        return false
      })

      if (!isEmpty(restrictedToMoveNodes)) {
        showErrorNotification({
          title: 'Move Error',
          description: (
            <div>
              The following nodes can&apos;t be moved:
              {map(restrictedToMoveNodes, ({ name }) => (
                <Typography.Paragraph style={{ margin: 0 }} key={name}>
                  {name};
                </Typography.Paragraph>
              ))}
            </div>
          ),
        })
      } else {
        dispatch(
          moveCuisineTypes({
            nodesToMove: parentSelectedNodes,
            destinationNode,
          }),
        )
      }
    },
    [dispatch],
  )

  const handleReplace = useCallback(
    async (nodesToReplace, replacementNodes) => {
      if (size(replacementNodes) > 1) {
        return showErrorNotification({
          title: 'Replace Error',
          description: "Can't Replace with more than 1 node",
        })
      }

      const [nodeToReplace] = nodesToReplace
      const [replacementNode] = replacementNodes

      if (nodeToReplace.isCurationEnabled) {
        return showErrorNotification({
          title: 'Replace Error',
          description:
            "The node can't be replaced as it's still enabled for curation",
        })
      }

      if (!isEmpty(replacementNode.replacedBy)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't replace as it's already being replaced by ${replacementNode.replacedBy[0].name}`,
        })
      }

      if (!isEmpty(nodeToReplace.replacedBy)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't be replaced as it's already being replaced by ${nodeToReplace.replacedBy[0].name}`,
        })
      }

      if (!isEmpty(nodeToReplace.replaces)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't be replaced as it's replacing other nodes ${join(
            map(nodeToReplace.replaces, 'name'),
            ',',
          )}`,
        })
      }

      await replaceCuisineType({
        idToReplace: nodeToReplace.id,
        replacementId: replacementNode.id,
      })

      return setIsReplaceActive(false)
    },
    [replaceCuisineType],
  )

  const handleResetTree = useCallback(
    () => dispatch(resetCuisineTypeTree()),
    [dispatch],
  )

  const handleSaveData = useCallback(
    () => saveCuisineTypes(data),
    [data, saveCuisineTypes],
  )

  const nodeTitleContentRender = useCallback(
    (node, searchValue) => {
      const nodeContentTitle = (
        <Link
          to={`${APP_ROUTES.CUISINE_TYPES.link}/${node.id}`}
          component={props => (
            <TypographyLinkWrapper
              {...props}
              target="_blank"
              strong={node.isErrorProne}
              disabled={isDirty}
            />
          )}
        >
          <SearchHighlightedSpan searchValue={searchValue}>
            {node.name}
          </SearchHighlightedSpan>
        </Link>
      )

      if (!isNil(node.replaceToken)) {
        return <StyledAlert message={nodeContentTitle} banner type="info" />
      }

      return nodeContentTitle
    },
    [isDirty],
  )

  useEffect(() => {
    if (saveError?.message || fetchError?.message || fetchLatestEventError) {
      showErrorNotification({
        title: 'Cuisine Type error',
        description:
          saveError?.message || fetchError?.message || fetchLatestEventError,
      })
    }
  }, [fetchError, fetchLatestEventError, saveError])

  return isFetchLoading ||
    isSaveLoading ||
    isFetchLatestEventLoading ||
    isEmpty(treeData) ? (
    <Spin />
  ) : (
    <>
      <StyledPageHeader>
        <StyledPageTitle>Cuisine Types</StyledPageTitle>
        <StyledPageActions>
          <StyledVerticalSpace>
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />

              <HistoryLink
                renderEvent={renderCuisineTypeEvent}
                filterEventTypes
                eventMetadataTypes={eventEntityTypes}
              />
            </Space>
            <FormButtons
              permissions={[PERMISSIONS.METADATA_TREE.SAVE]}
              isDisabled={isMoveActive || isReplaceActive ? true : !isDirty}
              onReset={handleResetTree}
              onSave={handleSaveData}
            />
          </StyledVerticalSpace>
        </StyledPageActions>
      </StyledPageHeader>
      <ReduxMetadataTree
        disableLink={!hasPermissionToViewDetails}
        mainTreeExpandedKeys={mainTreeExpandedKeys}
        supportingTreeExpandedKeys={supportingTreeExpandedKeys}
        expandMainTree={expandCuisineTypeMainTreeKeys}
        expandSupportingTree={expandCuisineTypeSupportingTreeKeys}
        metadataType={METADATA_TYPES.CUISINE_TYPES}
        isDirty={isDirty}
        showSearch
        allowReplaceMany={false}
        treeData={treeData}
        editable={hasFullPermission}
        addNode={handleAdd}
        deleteNode={handleDelete}
        renameNode={handleRename}
        moveNodes={handleMove}
        replaceNode={handleReplace}
        revertReplaceNode={handleRevertReplaceCuisineType}
        setDataForRenaming={handleSetDataForRenaming}
        setIsCurationEnabled={handleSetIsCurationEnabled}
        dataForRenaming={dataForRenaming}
        nodeTitleContentRender={nodeTitleContentRender}
        isMoveActive={isMoveActive}
        setIsMoveActive={setIsMoveActive}
        isReplaceActive={isReplaceActive}
        setIsReplaceActive={setIsReplaceActive}
      />
    </>
  )
}

export default CuisineTypesPage
